/**
 * Commitments — Mutual Commitment Ledger Liste mit Filtern + KPIs.
 *
 * @page /commitments
 * @roles admin, ops_manager, cs_teamlead, customer_success (read-team)
 * @api  /api/commitments, /api/commitments/stats
 * @i18n commitment.*
 * @since v2.408.0 / #930
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Handshake, Plus, Search, Filter, AlertTriangle, Clock, CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

function api(path, opts = {}) {
  const hasBody = opts.body != null;
  const headers = hasBody ? { 'Content-Type': 'application/json' } : {};
  return fetch(path, {
    ...opts,
    credentials: 'include',
    headers: { ...headers, ...opts.headers },
    body: hasBody ? JSON.stringify(opts.body) : undefined,
  }).then(async (r) => {
    let data;
    try { data = await r.json(); } catch { throw new Error(`HTTP ${r.status}`); }
    if (!r.ok) throw new Error(data.message || data.error || `HTTP ${r.status}`);
    return data;
  });
}

const STATUS_META = {
  draft:              { color: 'bg-[var(--bg-inset)] text-[var(--text-secondary)] border-[var(--border-default)]' },
  pending_internal:   { color: 'bg-amber-50 text-amber-700 border-amber-200' },
  rejected_internal:  { color: 'bg-rose-50 text-rose-700 border-rose-200' },
  pending_customer:   { color: 'bg-blue-50 text-blue-700 border-blue-200' },
  rejected_customer:  { color: 'bg-rose-50 text-rose-700 border-rose-200' },
  active:             { color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  expired:            { color: 'bg-[var(--bg-inset)] text-[var(--text-tertiary)] border-[var(--border-default)]' },
  terminated:         { color: 'bg-rose-50 text-rose-700 border-rose-200' },
  superseded:         { color: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
};

function StatusBadge({ status, t }) {
  const meta = STATUS_META[status] || STATUS_META.draft;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full border ${meta.color}`}>
      {t(`commitment.status.${status}`) || status}
    </span>
  );
}

function fmtDate(s) {
  if (!s) return '—';
  const d = String(s).slice(0, 10);
  if (!d) return '—';
  const [y, m, day] = d.split('-');
  return `${day}.${m}.${y}`;
}

export default function Commitments() {
  const { t } = useLanguage();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [statusFilter, setStatusFilter] = useState('alle');
  const [q, setQ] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'alle') params.set('status', statusFilter);
      if (q) params.set('q', q);
      const [list, s] = await Promise.all([
        api(`/api/commitments?${params}`),
        api('/api/commitments/stats'),
      ]);
      setItems(list.commitments || list || []);
      setStats(s || null);
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, q]);

  useEffect(() => { load(); }, [load]);

  const kpis = useMemo(() => {
    if (!stats) return null;
    return [
      { key: 'pending_internal', label: t('commitment.kpi.pending_internal'), value: stats.pending_internal || 0, icon: AlertTriangle, color: 'text-amber-600' },
      { key: 'pending_customer', label: t('commitment.kpi.pending_customer'), value: stats.pending_customer || 0, icon: Clock, color: 'text-blue-600' },
      { key: 'active', label: t('commitment.kpi.active'), value: stats.active || 0, icon: CheckCircle2, color: 'text-emerald-600' },
      { key: 'expiring_soon', label: t('commitment.kpi.expiring_soon'), value: stats.expiring_soon || 0, icon: Clock, color: 'text-rose-600' },
    ];
  }, [stats, t]);

  return (
    <div className="px-6 py-6 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Handshake className="w-6 h-6 text-[#338edc]" />
          <h1 className="text-2xl font-semibold text-[var(--text-primary)]">{t('commitment.page_title')}</h1>
        </div>
        <Link
          to="/commitments/new"
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#338edc] text-white text-sm font-medium hover:bg-[#287bc7]"
        >
          <Plus className="w-4 h-4" />
          {t('commitment.new')}
        </Link>
      </div>

      {kpis && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {kpis.map((k) => {
            const Ic = k.icon;
            return (
              <div key={k.key} className="bg-surface-card border border-line rounded-card p-4 flex items-center gap-3">
                <Ic className={`w-5 h-5 ${k.color}`} />
                <div>
                  <div className="text-2xl font-semibold text-[var(--text-primary)]">{k.value}</div>
                  <div className="text-xs text-[var(--text-secondary)]">{k.label}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="bg-surface-card border border-line rounded-card p-4">
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="w-4 h-4 absolute left-3 top-2.5 text-[var(--text-tertiary)]" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t('commitment.search_placeholder')}
              className="w-full pl-9 pr-3 py-2 rounded-lg border border-line bg-[var(--bg-inset)] text-sm"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-[var(--text-tertiary)]" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 rounded-lg border border-line bg-[var(--bg-inset)] text-sm"
            >
              <option value="alle">{t('commitment.filter.all')}</option>
              {Object.keys(STATUS_META).map((s) => (
                <option key={s} value={s}>{t(`commitment.status.${s}`) || s}</option>
              ))}
            </select>
          </div>
        </div>

        {err && <div className="px-3 py-2 mb-3 rounded-lg bg-rose-50 text-rose-700 text-sm">{err}</div>}
        {loading ? (
          <div className="flex items-center justify-center py-12 text-[var(--text-tertiary)]">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> {t('commitment.loading')}
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-12 text-[var(--text-tertiary)]">{t('commitment.empty')}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left border-b border-line text-[var(--text-secondary)] text-xs uppercase tracking-wide">
                  <th className="py-2 pr-3">{t('commitment.col.nr')}</th>
                  <th className="py-2 pr-3">{t('commitment.col.titel')}</th>
                  <th className="py-2 pr-3">{t('commitment.col.kunde')}</th>
                  <th className="py-2 pr-3">{t('commitment.col.kategorie')}</th>
                  <th className="py-2 pr-3">{t('commitment.col.gueltig_ab')}</th>
                  <th className="py-2 pr-3">{t('commitment.col.gueltig_bis')}</th>
                  <th className="py-2 pr-3">{t('commitment.col.flowoms_bearbeiter')}</th>
                  <th className="py-2 pr-3">{t('commitment.col.status')}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((c) => (
                  <tr key={c.id} className="border-b border-line hover:bg-[var(--bg-inset)] cursor-pointer odd:bg-[var(--bg-inset)]/30">
                    <td className="py-2 pr-3 font-mono text-xs">
                      <Link to={`/commitments/${c.id}`} className="text-[#338edc] hover:underline">{c.commitment_nr}</Link>
                    </td>
                    <td className="py-2 pr-3">
                      <Link to={`/commitments/${c.id}`} className="text-[var(--text-primary)] hover:underline">{c.titel}</Link>
                    </td>
                    <td className="py-2 pr-3 text-[var(--text-secondary)]">{c.kunde_name || `Kunde #${c.kunde_id}`}</td>
                    <td className="py-2 pr-3 text-[var(--text-secondary)]">{c.kategorie ? t(`commitment.kategorie.${c.kategorie}`) : '—'}</td>
                    <td className="py-2 pr-3 text-[var(--text-secondary)]">{fmtDate(c.gueltig_ab)}</td>
                    <td className="py-2 pr-3 text-[var(--text-secondary)]">{fmtDate(c.gueltig_bis)}</td>
                    <td className="py-2 pr-3 text-[var(--text-secondary)]">
                      {c.flowoms_bearbeiter_name
                        ? <span title={c.flowoms_bearbeiter_email || ''}>{c.flowoms_bearbeiter_name}</span>
                        : '—'}
                    </td>
                    <td className="py-2 pr-3"><StatusBadge status={c.status} t={t} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export { StatusBadge, STATUS_META, fmtDate };
